import React, { useState, useEffect } from 'react';
import {
  IconAlertCircle, IconMail, IconLock,
  IconEye, IconEyeOff, IconShieldCheck
} from '@tabler/icons-react';
import api from '../api/api';
import '../styles/LoginPage.css';

const SAVE_KEY = 'ai65-saved-credentials';

export default function LoginPage({ setIsLoggedIn }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [siteConfig, setSiteConfig] = useState({ 'site.name': 'AI65 Neo', 'site.description': '', 'site.loginColor': 'blue' });

  const LOGIN_COLOR_GRADIENTS = {
    blue:   'linear-gradient(135deg, #206bc4 0%, #4263eb 50%, #ae3ec9 100%)',
    green:  'linear-gradient(135deg, #2d6a4f 0%, #52b788 100%)',
    red:    'linear-gradient(135deg, #8b2635 0%, #c0392b 100%)',
    purple: 'linear-gradient(135deg, #4a2c8a 0%, #6741d9 100%)',
    black:  'linear-gradient(135deg, #212529 0%, #495057 100%)',
    gray:   'linear-gradient(135deg, #868e96 0%, #adb5bd 100%)',
  };
  const LOGIN_COLOR_PRIMARY = {
    blue: '#206bc4', green: '#2d6a4f', red: '#8b2635',
    purple: '#4a2c8a', black: '#212529', gray: '#868e96',
  };
  const loginColor = siteConfig['site.loginColor'] || 'blue';
  const loginGradient = LOGIN_COLOR_GRADIENTS[loginColor] || LOGIN_COLOR_GRADIENTS.blue;
  const loginPrimary = LOGIN_COLOR_PRIMARY[loginColor] || LOGIN_COLOR_PRIMARY.blue;

  // 사이트 설정 + 저장된 계정 불러오기
  useEffect(() => {
    api.get('/system-config').then(res => {
      setSiteConfig(prev => ({ ...prev, ...res.data }));
      if (res.data['site.favicon']) { let l = document.querySelector("link[rel~='icon']"); if (!l) { l = document.createElement('link'); l.rel = 'icon'; document.head.appendChild(l); } l.href = res.data['site.favicon']; }
      if (res.data['site.name']) document.title = res.data['site.name'] + ' - 관리자';
    }).catch(() => {});
    try {
      const saved = JSON.parse(localStorage.getItem(SAVE_KEY));
      if (saved?.email) {
        setEmail(saved.email);
        setPassword(saved.password || '');
        setRemember(true);
      }
    } catch {}
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { email, password });
      const { role, token } = res.data;

      if (role !== 'ADMIN' && role !== 'MASTER') {
        setError('관리자 계정만 접근 가능합니다.');
        return;
      }

      // 로그인 정보 저장/삭제
      if (remember) {
        localStorage.setItem(SAVE_KEY, JSON.stringify({ email, password }));
      } else {
        localStorage.removeItem(SAVE_KEY);
      }

      localStorage.setItem('token', token);
      if (res.data.refreshToken) localStorage.setItem('refreshToken', res.data.refreshToken);
      setIsLoggedIn(true);
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.response?.data?.message || '이메일 또는 비밀번호가 올바르지 않습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      background: 'var(--tblr-bg-surface, #f4f6fa)',
    }}>

      {/* 왼쪽 브랜딩 패널 */}
      <div style={{
        flex: 1,
        background: loginGradient,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px',
        color: '#fff',
        minWidth: 0,
      }}
        className="d-none d-lg-flex"
      >
        {/* 로고 영역 */}
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          {siteConfig['site.logo'] ? (
            <img src={siteConfig['site.logo']} alt="로고"
              style={{ width: '100%', maxWidth: 300, maxHeight: 100, borderRadius: 20, objectFit: 'contain', margin: '0 auto 24px', display: 'block' }} />
          ) : (
            <div style={{
              width: 80, height: 80, borderRadius: 20,
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: 36, fontWeight: 900,
              border: '2px solid rgba(255,255,255,0.3)',
            }}>
              {siteConfig['site.name']?.[0] || 'A'}
            </div>
          )}
          <h1 style={{ fontSize: 36, fontWeight: 800, marginBottom: 12, letterSpacing: '-0.5px' }}>
            {siteConfig['site.name']}
          </h1>
          <p style={{ fontSize: 16, opacity: 0.85, lineHeight: 1.7, marginBottom: 40, whiteSpace: 'pre-line' }}>
            {siteConfig['site.description'] || `${siteConfig['site.name']} 관리자 시스템에 오신 것을 환영합니다.\n사용자 관리, 시스템 설정, 통계를 한 곳에서.`}
          </p>

          {/* 기능 목록 */}
          {[
            '사용자 계정 관리 및 권한 설정',
            '실시간 통계 및 현황 모니터링',
            '다크모드 · 커스텀 테마 지원',
          ].map((text, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              marginBottom: 12, textAlign: 'left',
            }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%',
                background: 'rgba(255,255,255,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <IconShieldCheck size={14} />
              </div>
              <span style={{ fontSize: 14, opacity: 0.9 }}>{text}</span>
            </div>
          ))}
        </div>

        {/* 하단 카피라이트 */}
        <div style={{ marginTop: 'auto', opacity: 0.6, fontSize: 13 }}>
          © {new Date().getFullYear()} {siteConfig['site.name']}. All rights reserved.
        </div>
      </div>

      {/* 오른쪽 로그인 폼 */}
      <div style={{
        width: '100%', maxWidth: 480,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '48px 40px',
        background: 'var(--tblr-bg-surface, #fff)',
      }}>

        {/* 모바일 로고 */}
        <div className="d-lg-none text-center mb-4">
          <span style={{ fontSize: 28, fontWeight: 800, color: 'var(--tblr-primary, #206bc4)' }}>
            {siteConfig['site.name']}
          </span>
          <div className="text-muted small mt-1">관리자 콘솔</div>
        </div>

        <div style={{ width: '100%', maxWidth: 360 }}>
          <div className="mb-4">
            <h2 style={{ fontWeight: 700, fontSize: 24, marginBottom: 4 }}>
              로그인
            </h2>
            <p className="text-muted" style={{ fontSize: 14 }}>
              관리자 계정으로 로그인하세요.
            </p>
          </div>

          {/* 에러 메시지 */}
          {error && (
            <div className="alert alert-danger mb-4" role="alert"
              style={{ borderRadius: 10, border: 'none', background: '#fff0f0' }}>
              <div className="d-flex align-items-center gap-2">
                <IconAlertCircle size={18} style={{ color: '#d63939', flexShrink: 0 }} />
                <span style={{ fontSize: 14 }}>{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>

            {/* 아이디 */}
            <div className="mb-3">
              <label className="form-label fw-medium" style={{ fontSize: 14 }}>
                아이디
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                  color: 'var(--tblr-muted, #9aa0ac)', pointerEvents: 'none',
                }}>
                  <IconMail size={17} />
                </span>
                <input
                  type="text"
                  className="form-control login-input-with-icon"
                  placeholder="아이디를 입력하세요"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="off"
                  style={{
                    height: 44,
                    borderRadius: 10, fontSize: 14,
                    border: '1.5px solid var(--tblr-border-color, #e6e7e9)',
                    transition: 'border-color 0.15s',
                  }}
                  onFocus={e => e.target.style.borderColor = 'var(--tblr-primary, #206bc4)'}
                  onBlur={e => e.target.style.borderColor = 'var(--tblr-border-color, #e6e7e9)'}
                />
              </div>
            </div>

            {/* 비밀번호 */}
            <div className="mb-3">
              <label className="form-label fw-medium" style={{ fontSize: 14 }}>
                비밀번호
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                  color: 'var(--tblr-muted, #9aa0ac)', pointerEvents: 'none',
                }}>
                  <IconLock size={17} />
                </span>
                <input
                  type={showPw ? 'text' : 'password'}
                  className="form-control login-input-with-icon-right"
                  placeholder="비밀번호를 입력하세요"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="off"
                  style={{
                    height: 44,
                    borderRadius: 10, fontSize: 14,
                    border: '1.5px solid var(--tblr-border-color, #e6e7e9)',
                    transition: 'border-color 0.15s',
                  }}
                  onFocus={e => e.target.style.borderColor = 'var(--tblr-primary, #206bc4)'}
                  onBlur={e => e.target.style.borderColor = 'var(--tblr-border-color, #e6e7e9)'}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  style={{
                    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                    color: 'var(--tblr-muted, #9aa0ac)',
                  }}
                  tabIndex={-1}
                >
                  {showPw ? <IconEyeOff size={17} /> : <IconEye size={17} />}
                </button>
              </div>
            </div>

            {/* 로그인 정보 저장 */}
            <div className="mb-4 d-flex align-items-center">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  className="form-check-input m-0"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  style={{ width: 17, height: 17, borderRadius: 4, cursor: 'pointer' }}
                />
                <span style={{ fontSize: 14 }}>로그인 정보 저장</span>
              </label>
            </div>

            {/* 로그인 버튼 */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', height: 46,
                borderRadius: 10, fontSize: 15, fontWeight: 600,
                background: loading
                  ? 'var(--tblr-muted, #9aa0ac)'
                  : loginPrimary,
                border: 'none', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : '0 4px 12px rgba(32,107,196,0.35)',
                transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {loading ? (
                <><span className="spinner-border spinner-border-sm" style={{ width: 18, height: 18 }} /> 로그인 중...</>
              ) : '로그인'}
            </button>

          </form>

          {/* 안내 */}
          <div className="text-center mt-4" style={{ fontSize: 12, color: 'var(--tblr-muted, #9aa0ac)' }}>
            관리자 계정이 없다면 시스템 관리자에게 문의하세요.
          </div>
        </div>

        {/* 모바일 카피라이트 */}
        <div className="d-lg-none mt-auto pt-4 text-muted" style={{ fontSize: 12 }}>
          © {new Date().getFullYear()} {siteConfig['site.name']}
        </div>
      </div>

    </div>
  );
}
